import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGlobalStore } from '../../../application/store/store';
import { axiosInstance } from '../../../common/axiosInstans';
import isEqual from 'fast-deep-equal';
import { StorageLocation } from '../../../application/types/storageLocation.ts';
import { TextField, InputAdornment, IconButton, Box } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { StorageType } from '../../../application/types/storageType';
import { TransactionType } from '../../../application/types/transactionType';

interface AvailableStoragesProps {
    label: string;
    searchTerm: string;
    setSearchTerm: (searchTerm: string) => void;
    handleSelect: (storage: StorageLocation) => void;
    handleSelectEmergency: (emergency: StorageLocation) => void;
    className?: string;
    storageType:
        | StorageType.ambulance
        | StorageType.hospital
        | StorageType.storage;
    transactionType: TransactionType;
}

const AvailableStorages: React.FC<AvailableStoragesProps> = ({
    label,
    searchTerm,
    setSearchTerm,
    handleSelect,
    handleSelectEmergency,
    className,
    storageType,
}) => {
    const availableStorages = useGlobalStore(
        (state) => state.availableStorages
    );
    const emergencies = useGlobalStore((state) => state.emergencies);
    const selectedStorage = useGlobalStore((state) => state.selectedStorage);
    const selectedEmergency = useGlobalStore(
        (state) => state.selectedEmergency
    );
    const setAvailableStorages = useGlobalStore(
        (state) => state.setAvailableStorages
    );
    const setEmergencies = useGlobalStore((state) => state.setEmergencies);
    const setSelectedStorage = useGlobalStore(
        (state) => state.setSelectedStorage
    );
    const setSelectedEmergency = useGlobalStore(
        (state) => state.setSelectedEmergency
    );

    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isOpen, setIsOpen] = useState(false);

    const memoizedAvailableStorages = useMemo(
        () => availableStorages,
        [availableStorages]
    );
    const memoizedEmergencies = useMemo(() => emergencies, [emergencies]);

    const fetchAvailableStorages = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            let endpoint: string;
            switch (storageType) {
                case StorageType.ambulance:
                    endpoint = '/storagelocation/satelite';
                    break;
                case StorageType.hospital:
                    endpoint = '/storagelocation/nonesatelite';
                    break;
                case StorageType.storage:
                    endpoint = '/storagelocation';
                    break;
                default:
                    throw new Error(`Invalid storage type: ${storageType}`);
            }

            // OrganizationId is now automatically retrieved from JWT token on the server
            const response = await axiosInstance.get(endpoint);
            if (
                storageType === StorageType.hospital ||
                storageType === StorageType.storage
            ) {
                if (!isEqual(response.data, memoizedAvailableStorages)) {
                    setAvailableStorages(response.data);
                }
            } else if (storageType === StorageType.ambulance) {
                if (!isEqual(response.data, memoizedEmergencies)) {
                    setEmergencies(response.data);
                }
            }
        } catch (error) {
            console.error(`Error fetching storages for ${storageType}:`, error);
            setError(
                `Det gick inte att hämta ${storageType === StorageType.ambulance ? 'ambulanser' : 'lagerställen'}`
            );
        } finally {
            setIsLoading(false);
        }
    }, [
        storageType,
        memoizedAvailableStorages,
        memoizedEmergencies,
        setAvailableStorages,
        setEmergencies,
    ]);

    useEffect(() => {
        fetchAvailableStorages();
    }, [fetchAvailableStorages]);

    useEffect(() => {
        if (
            storageType === StorageType.hospital ||
            storageType === StorageType.storage
        ) {
            if (selectedStorage) {
                setSearchTerm(selectedStorage.name);
            }
        } else {
            if (selectedEmergency) {
                setSearchTerm(selectedEmergency.name);
            }
        }
    }, [selectedStorage, selectedEmergency, storageType, setSearchTerm]);

    const handleFocus = () => {
        setIsOpen(true);
    };

    const handleBlur = (e: React.FocusEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsOpen(false);
        }
    };

    const filteredStorages = useMemo(() => {
        const storages =
            storageType === StorageType.hospital ||
            storageType === StorageType.storage
                ? memoizedAvailableStorages
                : memoizedEmergencies;

        return searchTerm
            ? storages.filter((storage) =>
                  storage.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
            : storages;
    }, [
        memoizedAvailableStorages,
        memoizedEmergencies,
        searchTerm,
        storageType,
    ]);

    const handleSelectStorage = (storage: StorageLocation) => {
        setSearchTerm(storage.name);
        if (
            storageType === StorageType.hospital ||
            storageType === StorageType.storage
        ) {
            setSelectedStorage(storage);
            handleSelect(storage);
        } else {
            setSelectedEmergency(storage);
            handleSelectEmergency(storage);
        }
        setIsOpen(false);
    };

    const handleClear = () => {
        setSearchTerm('');
        if (
            storageType === StorageType.hospital ||
            storageType === StorageType.storage
        ) {
            setSelectedStorage(null);
        } else {
            setSelectedEmergency(null);
        }
    };

    return (
        <Box
            className={className}
            sx={{ width: '100%', textAlign: 'center', px: { xs: 0, sm: 0 } }}
        >
            {error && <p className="text-red-500">{error}</p>}

            <div className="relative w-full" onBlur={handleBlur}>
                <TextField
                    label={label}
                    fullWidth
                    placeholder="Sök lagerställe"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={handleFocus}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    aria-controls="storage-list"
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    {searchTerm && (
                                        <IconButton
                                            aria-label="clear search"
                                            onClick={handleClear}
                                            edge="end"
                                            size="small"
                                        >
                                            <ClearIcon />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        aria-label="toggle dropdown"
                                        edge="end"
                                        size="small"
                                        onClick={() => {
                                            if (!isOpen && searchTerm) {
                                                // Clear search term to show all options
                                                setSearchTerm('');
                                            }
                                            setIsOpen(!isOpen);
                                        }}
                                    >
                                        <ArrowDropDownIcon
                                            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
                                        />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                {isOpen && (
                    <div
                        id="storage-list"
                        role="listbox"
                        className="absolute z-10 mt-1 left-0 right-0 bg-white shadow-lg rounded-lg max-h-60 overflow-auto border border-gray-300"
                    >
                        {isLoading ? (
                            <p className="p-4 text-center">Laddar...</p>
                        ) : filteredStorages.length > 0 ? (
                            filteredStorages.map((storage) => (
                                <div
                                    key={storage.id}
                                    onMouseDown={() =>
                                        handleSelectStorage(storage)
                                    }
                                    role="option"
                                    className="p-2 hover:bg-gray-100 cursor-pointer text-sm w-full text-left"
                                    aria-selected={
                                        storageType === StorageType.hospital ||
                                        storageType === StorageType.storage
                                            ? selectedStorage?.id === storage.id
                                            : selectedEmergency?.id ===
                                              storage.id
                                    }
                                >
                                    {storage.name}
                                </div>
                            ))
                        ) : (
                            <p className="p-4 text-center">
                                Inga lagerställen funna
                            </p>
                        )}
                    </div>
                )}
            </div>
        </Box>
    );
};

export default AvailableStorages;
